import { useEffect, useState } from "react";
import {
  Drawer, Box, Typography, Divider, Button, Select, MenuItem, Alert,
  Stepper, Step, StepLabel, Chip,
} from "@mui/material";
import { adminApi } from "rsa/adminApi";
import StatusChip from "../Components/StatusChip";

export default function RequestDetailDrawer({ id, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [reassignTo, setReassignTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = async () => {
    try {
      setError("");
      const [d, v] = await Promise.all([
        adminApi("GET", `/admin/rsa/requests/${id}/detail`),
        adminApi("GET", "/admin/vendors?status=approved"),
      ]);
      setDetail(d); setVendors(v);
    } catch (e) { setError(e.message); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const reassign = async () => {
    if (!reassignTo) return;
    setBusy(true); setError("");
    try {
      await adminApi("POST", `/admin/rsa/requests/${id}/reassign`, { vendorId: reassignTo });
      setNotice("Reassigned. This bypasses the normal claim race — a support action, not a normal dispatch.");
      await load();
      onChanged?.();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (!detail) {
    return (
      <Drawer anchor="right" open onClose={onClose}>
        <Box sx={{ width: 420, p: 3 }}>
          {error ? <Alert severity="error">{error}</Alert> : <Typography>Loading…</Typography>}
        </Box>
      </Drawer>
    );
  }

  const { request, timeline, ambulance, insurance } = detail;

  return (
    <Drawer anchor="right" open onClose={onClose}>
      <Box sx={{ width: 440, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">{request.request_ref}</Typography>
          <StatusChip value={request.status} />
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {request.service_type} · <StatusChip value={request.severity} />
        </Typography>

        <Typography variant="subtitle2" fontWeight={700}>Requester</Typography>
        <Typography variant="body2">{request.requester_name} · {request.requester_phone || "—"}</Typography>

        <Typography variant="subtitle2" fontWeight={700} mt={2}>Assigned vendor</Typography>
        <Typography variant="body2">{request.vendor_name || "Unassigned"} {request.vendor_phone ? `· ${request.vendor_phone}` : ""}</Typography>

        {(ambulance || insurance || request.fire_requested || request.police_requested) && (
          <>
            <Divider sx={{ my: 2 }} />
            {ambulance && (
              <Box mb={1}>
                <Typography variant="subtitle2" fontWeight={700}>Ambulance</Typography>
                <Chip size="small" label={ambulance.status} />
              </Box>
            )}
            {(request.fire_requested || request.police_requested) && (
              <Box mb={1}>
                <Typography variant="subtitle2" fontWeight={700}>Emergency services requested</Typography>
                <Box display="flex" gap={0.5} mt={0.5}>
                  {request.fire_requested && <Chip size="small" color="error" label="🚒 Fire brigade" />}
                  {request.police_requested && <Chip size="small" color="error" label="👮 Police" />}
                </Box>
              </Box>
            )}
            {insurance && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>Insurance</Typography>
                <Chip size="small" label={insurance.status} />
              </Box>
            )}
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" fontWeight={700} mb={1}>Timeline</Typography>
        <Stepper orientation="vertical" nonLinear activeStep={timeline.length}>
          {timeline.map((t, i) => (
            <Step key={i} completed active={false}>
              <StepLabel optional={<Typography variant="caption">{new Date(t.at).toLocaleString()}</Typography>}>
                {t.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Divider sx={{ my: 2 }} />
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Manual reassignment is a support override — it bypasses the normal atomic
          first-to-accept claim race. Use only for a stuck or expired request.
        </Alert>
        {notice && <Alert severity="success" sx={{ mb: 1.5 }}>{notice}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <Box display="flex" gap={1}>
          <Select size="small" displayEmpty value={reassignTo} onChange={(e) => setReassignTo(e.target.value)} sx={{ flex: 1 }}>
            <MenuItem value="" disabled>Choose a vendor…</MenuItem>
            {vendors.map((v) => <MenuItem key={v.id} value={v.id}>{v.business_name}</MenuItem>)}
          </Select>
          <Button variant="contained" color="warning" disabled={!reassignTo || busy} onClick={reassign}>
            Reassign
          </Button>
        </Box>

        <Button sx={{ mt: 3 }} fullWidth onClick={onClose}>Close</Button>
      </Box>
    </Drawer>
  );
}
